import * as React from 'react'
import { ReactElement } from 'react'

import { theme } from '@/theme'
import { cn } from '@/utils'

import { TSvgIcon } from '../types'
import { Button } from './Button'

export type TBannerType = 'info' | 'error'

const TYPE_TO_ACCENT: Record<TBannerType, string> = {
	info: theme.colors.blue,
	error: theme.colors.white,
}

const TYPE_TO_BACKGROUND: Record<TBannerType, string> = {
	info: theme.colors['blue-8'],
	error: theme.colors.error,
}

export const BannerContainer = ({
	className,
	type,
	...props
}: React.ComponentProps<'div'> & { type: TBannerType }) => {
	return (
		<div
			data-slot="alert"
			role="alert"
			className={cn(
				'flex flex-col rounded-10 bg-[var(--banner-background)] p-4',
				className,
			)}
			style={{
				'--banner-accent': TYPE_TO_ACCENT[type],
				'--banner-background': TYPE_TO_BACKGROUND[type],
			}}
			{...props}
		/>
	)
}

export const Banner = ({
	type,
	Icon,
	title,
	description,
	actionLabel,
	actionIcon,
	onAction,
}: {
	type: TBannerType
	Icon: TSvgIcon
	title?: string
	description?: string
	actionLabel: string
	actionIcon?: ReactElement
	onAction: () => void
}) => {
	const icon = <Icon className="size-6 shrink-0 text-[var(--banner-accent)]" />

	const _title = title ? (
		<p data-slot="alert-title" className={cn('type-body-input-title')}>
			{title}
		</p>
	) : null

	const _description = description ? (
		<div data-slot="alert-description" className={cn('type-body-medium')}>
			{description}
		</div>
	) : null

	const desktopContent = (
		<div
			className="hidden items-center gap-2 desktop:flex"
			data-testid="banner-desktop"
		>
			{icon}
			<div className="flex flex-col items-start">
				{_title}
				{_description}
			</div>
		</div>
	)

	const mobileContent = title ? (
		<>
			<div
				className="flex items-center gap-2 desktop:hidden"
				data-testid="banner-mobile"
			>
				{icon}
				{_title}
			</div>
			<div className="desktop:hidden">{_description}</div>
		</>
	) : (
		<div className="flex items-center gap-2 desktop:hidden">
			{icon}
			{_description}
		</div>
	)

	return (
		<div
			data-slot="alert"
			role="alert"
			className={cn(
				'flex flex-col justify-between rounded-10 bg-[var(--banner-background)] p-4 desktop:flex-row desktop:items-center desktop:gap-6',
				type === 'error' && 'text-white',
			)}
			style={{
				'--banner-accent': TYPE_TO_ACCENT[type],
				'--banner-background': TYPE_TO_BACKGROUND[type],
			}}
		>
			{desktopContent}
			{mobileContent}

			<Button
				visual={type === 'error' ? 'ghost' : undefined}
				onClick={onAction}
				className={cn(
					'mt-[10px] desktop:mt-0',
					type === 'error' && 'hover:bg-white',
				)}
			>
				{actionIcon || null}
				{actionLabel}
			</Button>
		</div>
	)
}
